import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const workSteps = [
  {
    icon: 'work-icon-01.svg',
    title: 'Step 1 - Content Audit and Experience Discovery',
    desc: 'We review your current site, content types, publishing workflows, SEO performance, and stakeholder needs to define a growth-ready content strategy.',
  },
  {
    icon: 'work-icon-02.svg',
    title: 'Step 2 - Content Architecture and Governance Design',
    desc: 'We design the content model, information architecture, taxonomy, roles, approvals, localization strategy, and content lifecycle rules for consistency at scale.',
  },
  {
    icon: 'work-icon-03.svg',
    title: 'Step 3 - Platform Selection and Solution Architecture',
    desc: 'We choose the right CMS approach (traditional, headless, hybrid, composable) and design integrations, deployment, security, and performance foundations.',
  },
  {
    icon: 'work-icon-04.svg',
    title: 'Step 4 - Build, Integrate, and Migrate',
    desc: 'We implement templates and components, connect search and analytics, migrate content safely, and validate structured data, redirects, and editorial workflows.',
  },
  {
    icon: 'work-icon-05.svg',
    title: 'Step 5 - Launch, Optimization, and Enablement',
    desc: 'We launch with monitoring and release controls, optimize Core Web Vitals and conversion paths, and enable teams with training, documentation, and governance.',
  },
];

const faqItems = [
  {
    question: 'Should we choose headless, traditional, or hybrid CMS?',
    answer:
      'It depends on your channels, team structure, and speed requirements. Traditional CMS fits simpler sites with tight coupling between content and pages. Headless CMS is best when you publish to multiple channels (web, app, email, portals) and want reusable content. Hybrid works well when marketing teams want page building and preview, while engineering needs structured content and APIs. We help you select based on workflows, scale, SEO requirements, and long-term maintainability.',
  },
  {
    question: 'How do you handle SEO during a CMS rebuild or migration?',
    answer:
      'SEO is treated as a release-critical requirement. We plan URL governance, redirects, canonical rules, metadata, sitemaps, structured data, internal linking, and indexing controls. We also validate page speed and Core Web Vitals, and we use staging environments with crawl checks before go-live to prevent traffic loss.',
  },
  {
    question: 'Can you migrate content from WordPress, Drupal, or legacy CMS platforms?',
    answer:
      'Yes. We migrate content using a structured approach: mapping content types, cleaning inconsistent fields, preserving media assets, enforcing new taxonomies, and validating redirects. We also build repeatable scripts for re-runs, which is essential when migrations are staged or when teams keep publishing during the transition.',
  },
  {
    question: 'How do you design content models that scale with growth?',
    answer:
      'We model content around business entities and reuse, not around pages. That means defining content types, relationships, references, and taxonomies so content can be repurposed across landing pages, product pages, help centers, and emails. We also define governance rules, validation, and structured fields so content remains consistent across teams and regions.',
  },
  {
    question: 'Do you support multi-site and multi-language content operations?',
    answer:
      'Yes. We design localization workflows including translation handoffs, region-specific variants, fallback rules, and approval paths. For multi-site setups, we define shared components and shared content libraries so brands and regions can move fast without breaking standards.',
  },
  {
    question: 'What integrations are common in modern CMS and DXP builds?',
    answer:
      'Common integrations include search (site search and documentation search), analytics, consent and tag management, CRM and marketing automation, personalization, DAM, eCommerce platforms, and support knowledge bases. We design these integrations to be secure, observable, and resilient, with clear ownership and fallback behavior.',
  },
];

const ServiceCMS = () => {
  useEffect(() => {
    if (window.WOW) new window.WOW().init();
    if (window.initMagnetic) window.initMagnetic();
  }, []);

  return (
    <>
      <section className="breadcrumbs">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-wrapper">
                <div className="breadcrumb-cnt">
                  <span>Services Details</span>
                  <h1>CMS and Digital Experience Platforms</h1>
                  <div className="breadcrumb-list">
                    <Link to="/">Home</Link>
                    <img src="/assets/img/content/breadcrumb-arrow.svg" alt="" />
                    Services Details
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="service-details">
        <div className="about-services sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="about-content">
                  <div className="section-title-5">
                    <h2>
                      CMS and Digital <br /> Experience Platforms
                    </h2>
                  </div>

                  <p>
                    We design and build CMS platforms that support growth, speed, and governance. That includes traditional CMS, headless, hybrid, and composable architectures depending on your team structure and publishing needs. The goal is a content system that helps marketing, product, and operations teams ship content confidently without creating long-term technical debt.
                  </p>

                  <p>
                    Our approach combines content architecture, engineering, and SEO discipline. We help you model content for reuse, implement editorial workflows and approvals, and deliver a fast digital experience that performs well across search, performance metrics, and conversion.
                  </p>

                  <ul className="about-featue">
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Headless, hybrid, and composable CMS architectures
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Content modeling, taxonomy, and information architecture
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      SEO foundations: redirects, metadata, structured data
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Editorial workflows, approvals, and governance controls
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Localization and multi-site operations support
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Performance optimization and delivery via CDN and caching
                    </li>
                  </ul>
                </div>
              </div>

              <div
                className="col-lg-6 wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="service-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/laptop.png" alt="Service" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="how-we-work-section sec-mar">
          <div className="container">
            <div
              className="row mb-60 wow animate fadeInUp"
              data-wow-delay="200ms"
              data-wow-duration="1500ms"
            >
              <div className="col-lg-12">
                <div className="section-title">
                  <h2>How We Work</h2>
                </div>
              </div>
            </div>
          </div>

          <div className="work-item">
            <div className="container-fluid">
              <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1 gy-5 justify-content-center">
                {workSteps.map((step, idx) => (
                  <div
                    key={step.title}
                    className="col magnetic-item wow animate fadeInUp"
                    data-wow-delay={`${200 + idx * 100}ms`}
                    data-wow-duration="1500ms"
                  >
                    <div className="single-work">
                      <div className="work-icon">
                        <img src={`/assets/img/content/${step.icon}`} alt="" />
                      </div>
                      <div className="work-content">
                        <h3>{step.title}</h3>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="service-sort-driscription-area sec-mar">
          <div className="container">
            <div className="row">
              <div
                className="col-lg-12 wow animate fadeInUp"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="driscription-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/rectangular_image.png" alt="Description" />
                </div>
              </div>

              <div className="driscription-content">
                <div className="row">
                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="200ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      Modern CMS success depends on content architecture. We design content types and relationships so the same content can power landing pages, product pages, knowledge bases, and campaigns without duplication. This reduces publishing time, improves consistency, and makes personalization and experimentation feasible later.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Structured content models designed for reuse
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Taxonomy and tagging for search and navigation
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Editorial governance: roles, approvals, and auditability
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Preview, staging, and safe publishing workflows
                      </li>
                    </ul>

                    <p>
                      We also build with performance and accessibility in mind. That includes caching strategies, image optimization, careful script loading, and accessibility practices aligned to WCAG expectations so the experience is usable and compliant across audiences.
                    </p>
                  </div>

                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="300ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      CMS decisions vary by industry and operating model. High-growth SaaS teams typically need fast landing page iteration, feature launch content, and strong SEO governance. Regulated industries need strict review flows, controlled publishing permissions, and traceability. ECommerce and content publishers need performance and multi-variant operations at scale.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        SaaS and B2B: launch pages, docs hubs, conversion optimization
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        ECommerce: category pages, promotions, search, and performance tuning
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Healthcare and finance: approvals, disclaimers, audit-ready changes
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Education and media: multi-author workflows and structured libraries
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Global teams: localization, regional variants, multi-site governance
                      </li>
                    </ul>

                    <p>
                      Typical deliverables include a documented content model, component library and templates, migration and redirect plan, analytics and SEO setup, and an operating playbook that keeps publishing consistent as teams grow.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="service-faq-area sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/faqs.png" alt="FAQ" />
                </div>
              </div>

              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-content-area">
                  <h2>Frequently Asked Questions</h2>

                  <div className="accordion" id="accordionExample">
                    {faqItems.map((item, index) => (
                      <div key={item.question} className="accordion-item">
                        <h6 className="accordion-header" id={`heading${index}`}>
                          <button
                            className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse${index}`}
                            aria-expanded={index === 0 ? 'true' : 'false'}
                            aria-controls={`collapse${index}`}
                          >
                            {item.question}
                          </button>
                        </h6>

                        <div
                          id={`collapse${index}`}
                          className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                          aria-labelledby={`heading${index}`}
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">{item.answer}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceCMS;



